import React, { useEffect, useRef, useState } from 'react'
import { Icon } from '@pff-consumer/tweed-ui/atoms/icon/icon.web'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { SVGGroup } from '@pff-consumer/utils'
import { useStyles } from './promo-code.styles'
import { PromoCodeProps } from './promo-code.props'
import { LEGACY_CHECKOUT_URL } from '../../shared/constants'

const { useCheckoutActions, usePromoCode } = checkoutHooks

export const PromoCode = (props: PromoCodeProps) => {
  const { isCouponValid, isDiscountApplied = false } = props
  const { updatePromoCode } = useCheckoutActions()
  const savedPromoCode = usePromoCode()
  const [isActive, setIsActive] = useState<boolean>(false)
  const [promoCode, setPromoCode] = useState<string>(savedPromoCode)
  const styles = useStyles({ isCouponValid, isActive, isDiscountApplied, promoCode })
  const inputRef = useRef<HTMLInputElement>(null)
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const activeTabIndex = isActive ? 0 : -1

  /**
   * When user decides to login after applying promo code, this useeffect will restore the promocode
   */
  useEffect(() => {
    if (savedPromoCode) {
      setPromoCode(savedPromoCode)
      setIsActive(!!savedPromoCode)
    }
  }, [savedPromoCode])

  const togglePromocodeAccordion = () => {
    setIsActive(!isActive)
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Promo Code',
      ctaLocation: 'Single Page Checkout',
    })
  }

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value
    setPromoCode(value)
    if (value === '') {
      updatePromoCode('')
    }
  }

  const clearPromoCode = () => {
    setPromoCode('')
    updatePromoCode('')
  }

  const onClearKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      clearPromoCode()
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }

  const applyPromoCode = () => {
    updatePromoCode(promoCode)
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Apply',
      ctaLocation: 'Single Page Checkout',
    })
  }

  return (
    <div className={styles.container}>
      <button
        type='button'
        onClick={togglePromocodeAccordion}
        className={styles.promoCodeButton}
        data-analytics-id='promo-code-button'
      >
        <span className={styles.promoCodeText}>Promo Code</span>
        <Icon
          name={isActive ? 'collapse' : 'expand'}
          group={SVGGroup.Icons}
          height={25}
          width={25}
        />
      </button>
      <div className={styles.activeContainer}>
        <div className={styles.promoCodeContainer}>
          <div className={styles.promoCodeTextFieldContainer}>
            <input
              aria-label='Promo Code'
              className={styles.promoCodeTextField}
              tabIndex={activeTabIndex}
              value={promoCode}
              onChange={handlePromoCodeChange}
              ref={inputRef}
            />

            {promoCode ? (
              <span
                role='button'
                className={styles.promoCodeTextFieldClear}
                tabIndex={activeTabIndex}
                aria-label='Clear promo code'
                onClick={clearPromoCode}
                onKeyDown={onClearKeyDown}
                data-analytics-id='clear-promo-button'
              >
                Clear
              </span>
            ) : null}
          </div>
          <button
            type='button'
            className={styles.promoCodeApplyButton}
            onClick={applyPromoCode}
            tabIndex={activeTabIndex}
            disabled={isDiscountApplied || promoCode === ''}
            data-analytics-id='promo-code-apply-button'
            data-testid='promo-code-apply-button'
          >
            {isDiscountApplied ? 'Applied' : 'Apply'}
          </button>
        </div>
        {!isCouponValid ? (
          <div className={styles.errorContainer}>
            <Icon
              name='field-error'
              group={SVGGroup.Icons}
              height={12}
              width={12}
            />
            <span className={styles.errorText}>Promo code is not valid</span>
          </div>
        ) : null}
        {isDiscountApplied && (
          <div className={styles.errorContainer}>
            <Icon
              name='field-success'
              group={SVGGroup.Icons}
              height={12}
              width={12}
            />
            <span className={styles.successText}>Promo code applied successfully</span>
          </div>
        )}
        <div className={styles.promoCodeApplicabilityContainer}>
          <span className={styles.promoCodeApplicability}>Military, First Responder & Student 50% Discount</span>
          <div
            onClick={() => window.open(LEGACY_CHECKOUT_URL, '_blank')}
            onKeyDown={() => window.open(LEGACY_CHECKOUT_URL, '_blank')}
            className={styles.verifyButton}
            tabIndex={activeTabIndex}
            data-analytics-id='id-me-button'
            role='button'
            aria-label='Idme'
          >
            <Icon
              name='id-me'
              group={SVGGroup.Icons}
              height={25}
              width={128}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
